import { useTranslation } from 'react-i18next';

import type { CustomerAddressDto, CustomerDetailsDto } from 'types/api/customer.type';

import {
  formatCityProvinceCodeCountry,
  formatCustomerAddressLines,
  formatShippingMethod,
} from 'helpers/formatAddressDisplay';

import * as S from '../OrderAddressBlocks.styles';

type DetailRowData = { label: string; value: string };

type Translated = Readonly<{
  code?: string | null;
  descriptionIt?: string | null;
  descriptionEn?: string | null;
}>;

const pickDescription = (item: Translated, language: string) =>
  (language.startsWith('it') ? item.descriptionIt : item.descriptionEn) ||
  item.descriptionEn ||
  item.descriptionIt ||
  '';

function DetailRows({ rows }: Readonly<{ rows: DetailRowData[] }>) {
  return (
    <>
      {rows.map((r) => (
        <S.DetailRow key={r.label}>
          <S.DetailLabel>{r.label}</S.DetailLabel>
          <S.DetailValue>{r.value || '—'}</S.DetailValue>
        </S.DetailRow>
      ))}
    </>
  );
}

type CustomerDetailsPanelProps = Readonly<{
  details: CustomerDetailsDto | undefined;
  /** Buyer name / contact entered on the order (not the customer contact person). */
  buyerName?: string | null;
  buyerEmail?: string | null;
  /** Buyer Name / Contact and e-Mail rows (order page); the New / Copy Order modals have none. */
  withBuyer?: boolean;
}>;

export function CustomerDetailsPanel({
  details,
  buyerName,
  buyerEmail,
  withBuyer = true,
}: CustomerDetailsPanelProps) {
  const { i18n, t } = useTranslation();

  if (!details) {
    return (
      <S.DetailList data-testid="customer-details-panel">
        <S.DetailMuted>{t('customer.details_unavailable')}</S.DetailMuted>
      </S.DetailList>
    );
  }

  const { billingAddress, paymentType } = details;
  const paymentCondition = paymentType
    ? [paymentType.code, pickDescription(paymentType, i18n.language)]
        .filter(Boolean)
        .join(' - ')
    : '';

  const rows: DetailRowData[] = [
    { label: t('orderDetails.address'), value: billingAddress?.addressLine ?? '' },
    {
      label: t('orderDetails.cityStateCountry'),
      value: formatCityProvinceCodeCountry({
        zipCode: billingAddress?.zipCode,
        city: billingAddress?.city,
        subdivisionCode: billingAddress?.subdivisionCode,
        countryName: billingAddress?.countryName,
      }),
    },
    { label: t('orderDetails.paymentCondition'), value: paymentCondition },
  ];
  if (withBuyer) {
    rows.push(
      { label: t('orderDetails.buyerContact'), value: buyerName ?? '' },
      { label: t('orderDetails.email'), value: buyerEmail ?? '' }
    );
  }

  return (
    <S.DetailList data-testid="customer-details-panel">
      <DetailRows rows={rows} />
    </S.DetailList>
  );
}

type AddressDetailsPanelProps = Readonly<{
  address: CustomerAddressDto | null;
  emptyMessage: string;
  /** Adds the Shipping Method row (Shipping Address block only), read from `address.shippingMethod`. */
  withShippingMethod?: boolean;
  'data-testid'?: string;
}>;

export function AddressDetailsPanel({
  address,
  emptyMessage,
  withShippingMethod = false,
  'data-testid': testId,
}: AddressDetailsPanelProps) {
  const { i18n, t } = useTranslation();

  if (!address) {
    return (
      <S.DetailList data-testid={testId}>
        <S.DetailMuted>{emptyMessage}</S.DetailMuted>
      </S.DetailList>
    );
  }

  const rows: DetailRowData[] = [
    { label: t('orderDetails.address'), value: formatCustomerAddressLines(address) },
    {
      label: t('orderDetails.cityStateCountry'),
      value: formatCityProvinceCodeCountry(address),
    },
  ];

  if (withShippingMethod) {
    rows.push({
      label: t('orderDetails.shippingMethod'),
      value: formatShippingMethod(address.shippingMethod, i18n.language),
    });
  }

  return (
    <S.DetailList data-testid={testId}>
      <DetailRows rows={rows} />
    </S.DetailList>
  );
}
